import { EditOutlined } from "@ant-design/icons";
import { Col, Row, Tooltip, Typography } from "antd";
import { CARRIER_METHOD_LABEL, SHIPPING_TYPE } from "features/Cart/constants";
import { EDITABLE_SHIPPING_STATUSES, PAYMENT_STATUS, SHIPPING_VERSION } from "features/Order/constants";
import { useOrderPermissions } from "hooks/order";
import { t } from "i18next";
import { useState } from "react";
import { formatCurrency } from "utils/helperFuncs";
import ModalChangeShippingMethod from "../ModalChangeShippingMethod";

const DeliveryMethod = ({ dataInfoAgent, order, onRefetch }) => {
  const { shippingType, shippingTypeName, shippingConfiguration, shippingVersion } = dataInfoAgent;
  const [visibleChangeShippingMethod, setVisibleChangeShippingMethod] = useState(false);
  const { canUpdateShippingMethod } = useOrderPermissions();

  // Entry point sửa PTVC/ĐVVC. Luôn hiện icon, khoá kèm lý do thay vì ẩn — vận hành cần biết
  // tính năng có tồn tại và vì sao đơn này không sửa được, thay vì tưởng hệ thống không làm được.
  const getDisabledReason = () => {
    if (!canUpdateShippingMethod) return t("v2.order.detail.deliveryMethod.disabledByPermission");
    if (!EDITABLE_SHIPPING_STATUSES.includes(order?.status))
      return t("v2.order.detail.deliveryMethod.disabledByStatus");
    if (order?.paymentStatus === PAYMENT_STATUS.PAID) return t("v2.order.detail.deliveryMethod.disabledByPaid");
    return null;
  };

  const disabledReason = getDisabledReason();

  // Label bên trái set cứng px (đủ chứa "Phương thức giao hàng" — label dài nhất), bên phải flex
  // hết phần còn lại — thay cho Col span cố định theo lưới 24 cột (dễ bị cắt/lệch khi tên ĐVVC/PTVC
  // dài).
  const LABEL_WIDTH = 170;

  const renderInfoRow = (label, value) => (
    <Col span={24}>
      <Row wrap={false} align="middle">
        <Col style={{ width: LABEL_WIDTH, flexShrink: 0 }}>
          <Typography.Text strong>{label}</Typography.Text>
        </Col>
        <Col flex="auto">
          <Typography.Text strong style={{ color: "#052987" }}>
            {value}
          </Typography.Text>
        </Col>
      </Row>
    </Col>
  );

  // Cả 2 mảnh đều có thể thiếu: đơn V1 không có `carrierMethod`, đơn hoả tốc từ App Đại lý trả
  // `carrierMethod: null`. Ghép bằng `filter(Boolean)` rồi mới fallback "--" — bản cũ nội suy thẳng
  // vào template string nên `CARRIER_METHOD_LABEL[null]` ra chữ "undefined - Aha move".
  const renderDeliver3PartyName = () => {
    const config = shippingConfiguration?.deliver3PartyConfig;
    const parts =
      shippingVersion === SHIPPING_VERSION?.V2
        ? [CARRIER_METHOD_LABEL[config?.carrierMethod], config?.provider?.name]
        : [config?.provider?.name];
    return parts.filter(Boolean).join(" - ") || "--";
  };

  const renderInfoDeliveryMethod = {
    [`${SHIPPING_TYPE.BUS}`]: (
      <>
        {renderInfoRow(`${t("order.orderDetail.busName")}:`, shippingConfiguration?.busConfig?.busName)}
        {renderInfoRow(`${t("order.orderDetail.busPhone")}:`, shippingConfiguration?.busConfig?.telephone)}
        {renderInfoRow(`${t("order.orderDetail.busStation")}:`, shippingConfiguration?.busConfig?.busStation)}
      </>
    ),
    [`${SHIPPING_TYPE.DELIVER_3PARTY}`]: renderInfoRow(`${t("order.orderDetail.ĐVVC")}:`, renderDeliver3PartyName()),
    [`${SHIPPING_TYPE.DELIVER_3PARTY_INSTANT}`]: renderInfoRow(
      `${t("order.orderDetail.ĐVVC")}:`,
      renderDeliver3PartyName()
    ),
    [`${SHIPPING_TYPE.PICK_UP_COUNTER}`]: null,
    [`${SHIPPING_TYPE.URBAN_COD}`]: renderInfoRow(
      `${t("order.orderDetail.shippingFee")}:`,
      formatCurrency(shippingConfiguration?.urbanConfig?.fee)
    ),
  };

  return (
    <>
      <Col span={24}>
        <Row wrap={false} align="middle">
          <Col style={{ width: LABEL_WIDTH, flexShrink: 0 }}>
            <Typography.Text strong>{t("cart.deliveryMethodLabel")}</Typography.Text>
          </Col>
          <Col flex="auto">
            {/* Tên PTVC lấy thẳng từ BE (`order.shippingTypeName`) — đổi tên trong DB là UI đổi theo,
                FE không map cứng theo enum `shippingType` nữa. BE chưa trả thì hiện "--". */}
            <Typography.Text strong style={{ color: "#052987" }}>
              {shippingTypeName || "--"}
            </Typography.Text>
            <Tooltip title={disabledReason}>
              <EditOutlined
                onClick={disabledReason ? undefined : () => setVisibleChangeShippingMethod(true)}
                style={{
                  color: disabledReason ? "#9CADC4" : "#2563EB",
                  fontSize: 14,
                  marginLeft: 8,
                  cursor: disabledReason ? "not-allowed" : "pointer",
                }}
              />
            </Tooltip>
          </Col>
        </Row>
      </Col>
      {renderInfoDeliveryMethod[shippingType]}
      <ModalChangeShippingMethod
        visible={visibleChangeShippingMethod}
        onClose={() => setVisibleChangeShippingMethod(false)}
        order={order}
        onRefetch={onRefetch}
      />
    </>
  );
};

export default DeliveryMethod;
